/**
 * Paymaster設定
 *
 * ガス代スポンサーシップ（Paymaster）の設定を管理します。
 *
 * 推奨設定方法:
 * 1. Privy Dashboard (https://dashboard.privy.io) で Smart Wallets > Network Settings を開く
 * 2. 各ネットワーク（Sepolia, Mainnet等）に対してPaymaster URLを設定
 * 3. Privyが自動的にガス代をスポンサーします
 *
 * コード側でオーバーライドする場合:
 * 環境変数を設定してこのファイルの設定を使用します
 */

export type PaymasterProvider = 'pimlico' | 'alchemy' | 'biconomy' | 'zerodev' | 'none'

export interface PaymasterConfig {
  /** Paymasterが有効かどうか */
  enabled: boolean
  /** Paymasterプロバイダー */
  provider: PaymasterProvider
  /** paymasterContext（SmartWalletsProviderに渡す設定） */
  paymasterContext?: Record<string, unknown>
}

/**
 * 環境変数からPaymaster設定を取得
 */
function getPaymasterConfig(): PaymasterConfig {
  const provider = (import.meta.env.VITE_PAYMASTER_PROVIDER || 'none') as PaymasterProvider

  // プロバイダーが指定されていない場合は無効（Dashboard設定を使用）
  if (provider === 'none') {
    return {
      enabled: false,
      provider: 'none',
      paymasterContext: undefined,
    }
  }

  // Pimlico設定
  if (provider === 'pimlico') {
    const paymasterUrl = import.meta.env.VITE_PIMLICO_PAYMASTER_URL
    if (!paymasterUrl) {
      console.warn('[Paymaster] VITE_PIMLICO_PAYMASTER_URL is not set')
      return { enabled: false, provider: 'none' }
    }

    return {
      enabled: true,
      provider: 'pimlico',
      paymasterContext: {
        // Pimlicoはシンプルなスポンサーシップモード
        mode: 'sponsored',
      },
    }
  }

  // Alchemy設定
  if (provider === 'alchemy') {
    const gasPolicyId = import.meta.env.VITE_ALCHEMY_GAS_POLICY_ID
    if (!gasPolicyId) {
      console.warn('[Paymaster] VITE_ALCHEMY_GAS_POLICY_ID is not set')
      return { enabled: false, provider: 'none' }
    }

    return {
      enabled: true,
      provider: 'alchemy',
      paymasterContext: {
        policyId: gasPolicyId,
      },
    }
  }

  // Biconomy設定
  if (provider === 'biconomy') {
    return {
      enabled: true,
      provider: 'biconomy',
      paymasterContext: {
        mode: 'SPONSORED',
        calculateGasLimits: true,
        expiryDuration: 300,
        sponsorshipInfo: {
          webhookData: {},
          smartAccountInfo: {
            name: 'BICONOMY',
            version: '2.0.0',
          },
        },
      },
    }
  }

  // ZeroDev設定
  if (provider === 'zerodev') {
    return {
      enabled: true,
      provider: 'zerodev',
      paymasterContext: {
        // ZeroDevのデフォルト設定
      },
    }
  }

  return { enabled: false, provider: 'none' }
}

export const paymasterConfig = getPaymasterConfig()

/**
 * SmartWalletsProvider用のconfig
 * paymasterContextが設定されている場合のみ返す
 */
export function getSmartWalletsConfig(): { paymasterContext: Record<string, unknown> } | undefined {
  if (paymasterConfig.enabled && paymasterConfig.paymasterContext) {
    return {
      paymasterContext: paymasterConfig.paymasterContext,
    }
  }
  return undefined
}
